/**
 * ONE control for one intent, which expands to its destinations.
 *
 * A row that offers "Directions", "Apple Maps", "Waze" and "Copy address" as
 * four peer buttons has turned one intent into four decisions. Here the label
 * region fires the primary destination immediately and the chevron region
 * opens the alternates — a split hit area, with `aria-expanded` and
 * `aria-controls` on the chevron where they belong.
 *
 * An action is never collapsed. Only its alternates are, which is why this is
 * not a `CresultcardDisclosure`.
 */
"use client";
import { useCallback, useId, useState, type ReactElement } from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
import { IconChevronDown } from "@tabler/icons-react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import { CRC_R_PILL, CRC_TOUCH_TARGET } from "./cresultcard.tokens";
import { useCresultcardViewport } from "./cresultcard.viewport";
import {
  CresultcardActionGroupLayout,
  CresultcardActionTone,
  type CresultcardActionSpec,
} from "./cresultcard.types";
import { CresultcardAction } from "./CresultcardAction";
import { CresultcardShutter } from "./CresultcardShutter";

const CHEVRON_SIZE = 13;
const CHEVRON_STROKE = 1.5;
const TRAY_GAP = 6;
/**
 * The chevron's box, and where it sits at full width.
 *
 * Inline, the chevron is a sibling of the primary and the group is as wide as
 * the two together — which is correct, because the group is sized by its own
 * content. Stretched, it is not: the group filled 520px, the chevron took 30 of
 * them off the end, and the PRIMARY measured 488 against 520px secondaries. A
 * filled mint control drawn narrower than the quiet controls beneath it renders
 * the hierarchy upside down for the second time.
 *
 * So at full width the chevron stops being a sibling and rides the primary's own
 * trailing edge: the primary paints the full 520, the chevron sits over the
 * padding the icon well already reserves there, and the two hit areas stay
 * separate — which is the split this component exists for (§7.1).
 */
const CHEVRON_BOX = 30;
const CHEVRON_TRAILING_INSET = 2;
/** The alternates come from the control, so the tray grows from that corner. */
const TRAY_ORIGIN = "left top";
const MORE_LABEL_PREFIX = "More ways to ";
const CHEVRON_ROOT_ATTRIBUTES = {
  "data-el": "Cresultcard/ActionGroup/Trigger",
  "data-crc-control": true,
};

interface CresultcardActionGroupProps {
  readonly label: string;
  readonly icon?: TablerIcon;
  readonly tone: CresultcardActionTone;
  /** Fires on a direct press of the label region. */
  readonly primary: CresultcardActionSpec;
  readonly alternates: readonly CresultcardActionSpec[];
  readonly layout?: CresultcardActionGroupLayout;
}

export function CresultcardActionGroup({
  label,
  icon,
  tone,
  primary,
  alternates,
  layout = CresultcardActionGroupLayout.Inline,
}: CresultcardActionGroupProps): ReactElement {
  const [open, setOpen] = useState(false);
  const trayId = useId();
  const { isMobile } = useCresultcardViewport();
  const toggle = useCallback((): void => {
    setOpen((current) => !current);
  }, []);
  const fullWidth = layout === CresultcardActionGroupLayout.FullWidth;

  return (
    <div
      data-el="Cresultcard/ActionGroup/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: fullWidth ? "stretch" : "flex-start",
        gap: TRAY_GAP,
        minWidth: 0,
      }}
    >
      <div
        style={{
          position: fullWidth ? "relative" : undefined,
          display: "flex",
          alignItems: "center",
          gap: fullWidth ? 0 : 2,
          minWidth: 0,
        }}
      >
        <CresultcardAction
          fullWidth={fullWidth}
          href={primary.href}
          icon={icon}
          label={label}
          onPress={primary.onPress}
          tone={tone}
          trailingReserve={
            fullWidth && alternates.length > 0
              ? CHEVRON_BOX + CHEVRON_TRAILING_INSET
              : undefined
          }
          value={primary.value == null ? undefined : String(primary.value)}
        />
        {alternates.length > 0 && (
          <Cbutton
            ariaLabel={`${MORE_LABEL_PREFIX}${label.toLowerCase()}`}
            containerStyle={{
              minWidth: isMobile ? CRC_TOUCH_TARGET : CHEVRON_BOX,
              minHeight: isMobile ? CRC_TOUCH_TARGET : CHEVRON_BOX,
            }}
            disableClickMotion
            onClick={toggle}
            padding={0}
            preset={ButtonPreset.Ghost}
            radius={CRC_R_PILL}
            rootAttributes={{
              ...CHEVRON_ROOT_ATTRIBUTES,
              "aria-controls": trayId,
              "aria-expanded": open,
            }}
            style={
              fullWidth
                ? {
                    position: "absolute",
                    insetInlineEnd: CHEVRON_TRAILING_INSET,
                    insetBlockStart: "50%",
                    transform: "translateY(-50%)",
                  }
                : undefined
            }
          >
            <span
              className="cresultcard__chevron"
              data-crc-expanded={open}
              style={{ display: "inline-flex" }}
            >
              <IconChevronDown size={CHEVRON_SIZE} stroke={CHEVRON_STROKE} />
            </span>
          </Cbutton>
        )}
      </div>

      <CresultcardShutter
        id={trayId}
        open={open}
        // The tray comes FROM the control, so it grows from that edge.
        transformOrigin={TRAY_ORIGIN}
      >
        <div
          data-el="Cresultcard/ActionGroup/Alternates"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: TRAY_GAP,
            paddingTop: TRAY_GAP,
          }}
        >
          {alternates.map((alternate) => (
            <CresultcardAction
              compact
              href={alternate.href}
              icon={alternate.icon}
              key={alternate.label}
              label={alternate.label}
              onPress={alternate.onPress}
              tone={CresultcardActionTone.Quiet}
              value={
                alternate.value == null ? undefined : String(alternate.value)
              }
            />
          ))}
        </div>
      </CresultcardShutter>
    </div>
  );
}
